import { FC, useCallback, useMemo, useState } from "react"
import { buildRoute, RouteConfigItem } from "features/subscription-process/utils/getRouteByStepIdentifier";
import { useNavigate, useParams } from "react-router-dom";
import { useBorrowerRiskData, useSubscriptionProcessState } from "features/subscription-process/contexts/selectors/selectors";
import { StepIdentifier, SubscriptionStepDto } from "api/risk";

interface SubstepConfig {
  quoteId?: string;
  borrowerId?: string;
  label: string;
  route: string;
}

interface StepRouteSelectorTooltipProps {
  routeConfigs: RouteConfigItem[]
  className: string
  label: string
  disabled: boolean
  /** Substeps for contractualization step (quotes with quoteId) */
  substeps?: SubscriptionStepDto[]
}

export const StepRouteSelectorTooltip: FC<StepRouteSelectorTooltipProps> = ({
  routeConfigs,
  className,
  label,
  disabled,
  substeps
}) => {
  const navigate = useNavigate()
  const [showTooltip, setShowTooltip] = useState(false);
  const { riskId: contextRiskId, steps } = useSubscriptionProcessState()
  const borrowerRiskData = useBorrowerRiskData()
  // Fallback to URL params if context is not yet loaded
  const { riskId: urlRiskId, borrowerProjectId: urlBorrowerProjectId, borrowerId: urlBorrowerId } = useParams<{ riskId?: string; borrowerProjectId?: string; borrowerId?: string; }>();

  // Use context riskId first, then fallback to URL params
  const riskId = contextRiskId || urlRiskId || urlBorrowerProjectId || ''

  const contractualizationFirstSubstep = useMemo(() => {
    const contractualizationStep = steps.find(step => step.identifier === StepIdentifier.Contractualization)
    return contractualizationStep?.subSteps[0]
  }, [steps])

  const quoteId = contractualizationFirstSubstep?.data?.quoteId

  // borrowerId courant : URL en priorité, puis le devis en contractualisation, puis le 1er
  // emprunteur du projet (cas page "Création du dossier" où l'URL n'a pas de borrowerId).
  // Évite de générer une route Tarificateur/Contractualisation sans borrowerId (segment
  // optionnel supprimé -> URL incomplète).
  const resolvedBorrowerId =
    urlBorrowerId ||
    contractualizationFirstSubstep?.data?.borrowerId ||
    borrowerRiskData?.insureds?.[0]?.borrowerId

  // Build substep configs for substeps with quoteId or borrowerId
  const substepConfigs = useMemo((): SubstepConfig[] => {
    if (!substeps || substeps.length === 0 || routeConfigs.length === 0) return [];

    const baseRoute = routeConfigs[0].route;

    return substeps
      .filter(sub => sub.data?.quoteId || sub.data?.borrowerId)
      .map(sub => ({
        quoteId: sub.data?.quoteId,
        borrowerId: sub.data?.borrowerId,
        label: sub.data?.quoteIdentifier || sub.name,
        route: baseRoute
      }));
  }, [substeps, routeConfigs])

  const onNavigation = useCallback((route: string) => {
    const params: Record<string, string> = {
      ...(riskId && { riskId }),
      ...(urlBorrowerProjectId && { borrowerProjectId: urlBorrowerProjectId }),
      ...(resolvedBorrowerId && { borrowerId: resolvedBorrowerId }),
      ...(quoteId && { quoteId }),
    }

    const builtRoute = buildRoute(route, params)
    navigate(builtRoute)
  }, [riskId, navigate, quoteId, urlBorrowerProjectId, resolvedBorrowerId])

  const onSubstepNavigation = useCallback((substep: SubstepConfig) => {
    const params: Record<string, string> = {
      ...(riskId && { riskId }),
      ...(urlBorrowerProjectId && { borrowerProjectId: urlBorrowerProjectId }),
      ...(substep.borrowerId && { borrowerId: substep.borrowerId }),
      ...(substep.quoteId && { quoteId: substep.quoteId }),
    }

    const builtRoute = buildRoute(substep.route, params)
    navigate(builtRoute)
  }, [riskId, navigate, urlBorrowerProjectId])

  const handleMouseEnter = useCallback(() => {
    setShowTooltip(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false);
  }, []);

  if (routeConfigs.length === 0 || disabled) {
    return <div className="mt-6 absolute w-32">
      <span className={`flex text-xs justify-center px-1 leading-tight ${className}`}>
        {label}
      </span>
    </div>
  }

  // Single route with multiple substeps (quotes) - show dropdown
  if (routeConfigs.length === 1 && substepConfigs.length > 1) {
    return (
      <div
        className="mt-6 absolute w-32"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span className={`flex text-xs ${showTooltip && 'text-primary'} hover:cursor-pointer hover:text-primary justify-center px-1 leading-tight ${className}`}>
          {label}
        </span>
        {showTooltip && (
          <>
            <div className="h-2"></div>
            <div className="absolute z-10 left-1/2 -translate-x-1/2 w-64 bg-zinc-50 border border-gray-200 text-primary/80 text-sm rounded-lg shadow-lg p-1 animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="space-y-1.5">
                {substepConfigs.map((substep) =>
                  <button
                    key={substep.quoteId || substep.borrowerId}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-primary/10 hover:text-primary transition-colors duration-150 font-medium"
                    onClick={() => onSubstepNavigation(substep)}
                  >
                    {substep.label}
                  </button>
                )}
              </div>
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 border-t border-l border-gray-200 bg-zinc-50 rotate-45"></div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Single route without substeps or with only one substep - direct navigation
  if (routeConfigs.length === 1) {
    return <div
      className="mt-6 absolute w-32"
      onClick={() => onNavigation(routeConfigs[0].route)}
    >
      <span className={`flex hover:cursor-pointer hover:text-primary text-xs justify-center px-1 leading-tight ${className}`}>
        {label}
      </span>
    </div>
  }

  return (
    <div
      className="mt-6 absolute w-32"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className={`flex text-xs ${showTooltip && 'text-primary'} justify-center px-1 leading-tight ${className}`}>
        {label}
      </span>
      {showTooltip && (
        <>
          <div className="h-2"></div>
          <div className="absolute z-10 left-1/2 -translate-x-1/2 w-64 bg-zinc-50 border border-gray-200 text-primary/80 text-sm rounded-lg shadow-lg p-1  animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="space-y-1.5">
              {routeConfigs.map((config) =>
                <button key={config.label}
                  className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-primary/10 hover:text-primary transition-colors duration-150 font-medium"
                  onClick={() => onNavigation(config.route)}>
                  {config.label}
                </button>
              )}
            </div>
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 border-t border-l border-gray-200 bg-zinc-50 rotate-45"></div>
          </div>
        </>
      )}
    </div>
  );
};
